/**
 * BSI MESH Framework API Client
 *
 * Calls the backend /api/v1/mesh/* endpoints implementing the
 * Mapping, Evaluation, Strategy, Harmonization framework.
 */
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const BASE = `${API_BASE_URL}/api/v1/mesh`;

export type AnswerValue = 'Yes' | 'Partially' | 'No';

export interface MaturityAnswers {
  mapping: AnswerValue[];
  evaluation: AnswerValue[];
  strategy: AnswerValue[];
  harmonization: AnswerValue[];
}

export interface MaturityRequest {
  company_name: string;
  industry: string;
  annual_revenue: string;
  risk_tolerance: string;
  answers: MaturityAnswers;
}

export interface DimensionResult {
  label: string;
  score: number;
  maturity_level: string;
  description: string;
  gaps: string[];
  strengths: string[];
}

export interface MaturityResult {
  framework: string;
  assessment_date: string;
  company: string;
  industry: string;
  overall: { score: number; maturity_level: string; description: string };
  dimensions: Record<string, DimensionResult>;
  priority_actions: Array<{
    dimension: string;
    current_level: string;
    target_level: string;
    focus_area: string;
    effort: string;
    business_impact: string;
  }>;
  ai_interpretation: string | null;
}

export interface RiskScanRequest {
  company_name: string;
  industry: string;
  annual_revenue?: string;
  risk_tolerance?: string;
  risk_category: 'geopolitical' | 'esg_human_rights' | 'financial' | 'operational' | 'cyber';
  scenario: string;
  suppliers?: Array<{ name: string; country: string }>;
}

export interface RiskScanResult {
  framework: string;
  mesh_pillar: string;
  risk_category: string;
  risk_category_label: string;
  scenario: string;
  company: string;
  analysis_date: string;
  key_indicators: string[];
  analysis: string;
  related_categories: string[];
}

export interface SupplierNode {
  node_id: string;
  name: string;
  tier: number;
  country: string;
  region?: string;
  city?: string;
  products_or_services: string[];
  is_single_source: boolean;
  criticality: 'low' | 'medium' | 'high' | 'critical';
  parent_node_ids: string[];
  annual_spend?: number;
  risk_flags: string[];
}

export interface SupplyChainMap {
  framework: string;
  pillar: string;
  total_nodes: number;
  total_edges: number;
  tiers_mapped: number[];
  tier_breakdown: Record<number, number>;
  country_concentration: Record<string, number>;
  single_source_critical_count: number;
  nodes: SupplierNode[];
  edges: Array<{ from: string; to: string }>;
}

export interface GeoConcentrationResult {
  framework: string;
  pillar: string;
  total_nodes: number;
  total_spend: number;
  concentrations: Array<{
    country: string;
    node_count: number;
    node_share_pct: number;
    spend: number;
    spend_share_pct: number;
    tiers_present: number[];
    critical_node_count: number;
  }>;
  alerts: Array<{
    country: string;
    alert: string;
    mesh_risk_category: string;
    recommendation: string;
  }>;
}

// ===== Structured (typed JSON) AI outputs =====

export interface RiskFactor {
  factor: string;
  severity: number;
  likelihood: number;
  risk_score: number;
  mesh_indicator?: string;
}

export interface StrategyAction {
  action: string;
  priority: 'High' | 'Medium' | 'Low' | string;
  owner_role: string;
  timeline: string;
  estimated_cost_gbp?: number;
  estimated_impact_gbp?: number;
}

export interface MonitoringIndicator {
  indicator: string;
  frequency: string;
  owner: string;
  alert_threshold: string;
}

export interface StructuredRiskAnalysis {
  mesh_pillar: string;
  risk_category: string;
  overall_risk_level: 'Low' | 'Medium' | 'High' | 'Critical' | string;
  confidence: 'Low' | 'Medium' | 'High' | string;
  summary: string;
  primary_drivers: string[];
  time_horizon: string;
  risk_factors: RiskFactor[];
  strategy_recommendations: StrategyAction[];
  monitoring_indicators: MonitoringIndicator[];
  functions_to_involve: string[];
  escalation_trigger: string;
  assumptions: string[];
}

export interface StructuredRiskScanResult {
  framework: string;
  format: 'structured';
  mesh_pillar: string;
  risk_category: string;
  risk_category_label: string;
  scenario: string;
  company: string;
  analysis_date: string;
  key_indicators: string[];
  structured_analysis: StructuredRiskAnalysis;
}

export interface DimensionInsight {
  dimension: string;
  score: number;
  maturity_level: string;
  key_gap: string;
  industry_benchmark: string;
  key_strength: string;
}

export interface MaturityRoadmapItem {
  quarter: string;
  focus: string;
  deliverable: string;
  target_dimension: string;
}

export interface StructuredMaturityInterpretation {
  overall_score: number;
  overall_level: string;
  headline_finding: string;
  biggest_competitive_risk: string;
  dimension_insights: DimensionInsight[];
  twelve_month_roadmap: MaturityRoadmapItem[];
  quick_wins: string[];
}

export interface StructuredMaturityResult {
  framework: string;
  format: 'structured';
  company: string;
  industry: string;
  assessment_date: string;
  deterministic: {
    overall_score: number;
    overall_level: string;
    dimension_scores: Record<string, number>;
  };
  structured_interpretation: StructuredMaturityInterpretation;
}

export const meshApi = {
  getFramework: () => axios.get(`${BASE}/framework`).then(r => r.data),
  getRiskCategories: () => axios.get(`${BASE}/risk-categories`).then(r => r.data),
  runMaturityAssessment: (req: MaturityRequest) =>
    axios.post<MaturityResult>(`${BASE}/maturity-assessment`, req).then(r => r.data),
  runMaturityAssessmentStructured: (req: MaturityRequest) =>
    axios.post<StructuredMaturityResult>(`${BASE}/maturity-assessment/structured`, req).then(r => r.data),
  runRiskScan: (req: RiskScanRequest) =>
    axios.post<RiskScanResult>(`${BASE}/risk-scan`, req).then(r => r.data),
  runRiskScanStructured: (req: RiskScanRequest) =>
    axios.post<StructuredRiskScanResult>(`${BASE}/risk-scan/structured`, req).then(r => r.data),
  getMap: () => axios.get<SupplyChainMap>(`${BASE}/map`).then(r => r.data),
  getSingleSourceRisks: () => axios.get(`${BASE}/map/single-source-risk`).then(r => r.data),
  getGeoConcentration: () =>
    axios.get<GeoConcentrationResult>(`${BASE}/map/geo-concentration`).then(r => r.data),
  upsertNode: (node: SupplierNode) =>
    axios.post(`${BASE}/map/nodes`, node).then(r => r.data),
  deleteNode: (nodeId: string) =>
    axios.delete(`${BASE}/map/nodes/${nodeId}`).then(r => r.data),
  askAgent: (req: AgentAskRequest) =>
    axios.post<AgentAskResult>(`${BASE}/agent/ask`, req).then(r => r.data),
  streamAgent: (
    req: AgentAskRequest,
    handlers: {
      onConversation?: (conversation_id: string) => void;
      onToolCall?: (tool: string, args: Record<string, any>) => void;
      onToolResult?: (tool: string, result: any) => void;
      onText?: (delta: string) => void;
      onCitations?: (citations: Citation[]) => void;
      onDone?: (payload: { answer: string; iterations: number; ok: boolean; history: AgentTurn[]; conversation_id?: string }) => void;
      onError?: (msg: string) => void;
    },
    signal?: AbortSignal,
  ) => streamSSE(`${BASE}/agent/stream`, req, handlers, signal),
};

export interface Citation {
  marker: string;       // e.g. "#1"
  chunk_id: string;
  doc_id: string;
  title: string;
  tags: string[];
  score: number;
  preview: string;
}

async function streamSSE(
  url: string,
  body: any,
  handlers: {
    onConversation?: (conversation_id: string) => void;
    onToolCall?: (tool: string, args: Record<string, any>) => void;
    onToolResult?: (tool: string, result: any) => void;
    onText?: (delta: string) => void;
    onCitations?: (citations: Citation[]) => void;
    onDone?: (payload: { answer: string; iterations: number; ok: boolean; history: AgentTurn[]; conversation_id?: string }) => void;
    onError?: (msg: string) => void;
  },
  signal?: AbortSignal,
) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) {
    handlers.onError?.(`HTTP ${res.status}`);
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buf = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf('\n\n')) !== -1) {
      const raw = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const lines = raw.split('\n');
      let event = 'message';
      let data = '';
      for (const ln of lines) {
        if (ln.startsWith('event:')) event = ln.slice(6).trim();
        else if (ln.startsWith('data:')) data += ln.slice(5).trim();
      }
      if (!data) continue;
      let parsed: any;
      try { parsed = JSON.parse(data); } catch { continue; }
      switch (event) {
        case 'conversation':
          handlers.onConversation?.(parsed.conversation_id);
          break;
        case 'tool_call':
          handlers.onToolCall?.(parsed.tool, parsed.args || {});
          break;
        case 'tool_result':
          handlers.onToolResult?.(parsed.tool, parsed.result);
          break;
        case 'text':
          handlers.onText?.(parsed.delta || '');
          break;
        case 'citations':
          handlers.onCitations?.(parsed as Citation[]);
          break;
        case 'done':
          handlers.onDone?.(parsed);
          break;
        case 'error':
          handlers.onError?.(parsed.message || 'stream error');
          break;
      }
    }
  }
}

// ===== Agent =====
export interface AgentTurn {
  role: 'user' | 'model';
  text: string;
}

export interface AgentAskRequest {
  question: string;
  company_name?: string;
  industry?: string;
  history?: AgentTurn[];
  /** Pass an existing UUID to resume a thread, or "new" to auto-create one. */
  conversation_id?: string | null;
}

export interface AgentToolCall {
  tool: string;
  args: Record<string, any>;
  result: any;
}

export interface AgentAskResult {
  framework: string;
  agent: string;
  question: string;
  company: string;
  answer: string;
  tool_calls: AgentToolCall[];
  iterations: number;
  ok: boolean;
  history: AgentTurn[];
  citations?: Citation[];
  conversation_id?: string | null;
}
